// Skeleton — pulse animation on the recessed surface.
//
// Variants:
//   list  — N rows of varying widths, useful for sidebar / list views
//   card  — a single card-shaped block
//   table — a multi-row, multi-column grid
//
// Pulse via Tailwind's animate-pulse. `count` controls row count for
// list/table variants (default 3); ignored for card.

import { type HTMLAttributes } from "react";

import { cn } from "./cn";

export type SkeletonVariant = "list" | "card" | "table";

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant;
  count?: number;
}

// Tailwind generates these arbitrary widths fine since we use a token
// repeating pattern (no hex). Widths are visual variety only.
const LIST_ROW_WIDTHS = ["w-3/4", "w-2/3", "w-5/6", "w-1/2", "w-3/4"];

function Bar({ widthClass = "w-full" }: { widthClass?: string }) {
  return (
    <div
      data-testid="skeleton-bar"
      className={cn("h-3 rounded-sm bg-recessed animate-pulse", widthClass)}
    />
  );
}

export function Skeleton({
  variant = "list",
  count = 3,
  className,
  ...rest
}: SkeletonProps) {
  if (variant === "card") {
    return (
      <div
        data-variant="card"
        data-testid="skeleton-root"
        className={cn(
          "rounded-lg border border-border-subtle bg-raised p-5 space-y-3",
          className,
        )}
        {...rest}
      >
        <Bar widthClass="w-1/3" />
        <Bar widthClass="w-full" />
        <Bar widthClass="w-5/6" />
        <Bar widthClass="w-2/3" />
      </div>
    );
  }

  if (variant === "table") {
    const rows = Math.max(1, count);
    return (
      <div
        data-variant="table"
        data-testid="skeleton-root"
        className={cn("flex flex-col gap-2", className)}
        {...rest}
      >
        {Array.from({ length: rows }).map((_, rowIdx) => (
          <div
            key={rowIdx}
            data-testid="skeleton-row"
            className="grid grid-cols-4 gap-3"
          >
            <Bar widthClass="w-full" />
            <Bar widthClass="w-full" />
            <Bar widthClass="w-full" />
            <Bar widthClass="w-full" />
          </div>
        ))}
      </div>
    );
  }

  // list
  const rows = Math.max(1, count);
  return (
    <div
      data-variant="list"
      data-testid="skeleton-root"
      className={cn("flex flex-col gap-3", className)}
      {...rest}
    >
      {Array.from({ length: rows }).map((_, idx) => (
        <Bar
          key={idx}
          widthClass={LIST_ROW_WIDTHS[idx % LIST_ROW_WIDTHS.length]}
        />
      ))}
    </div>
  );
}
